import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, Award } from 'lucide-react';

const STATUS_STYLES = {
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  not_started: ''
};

const ResetUserSelector = ({ departments, department, onDepartmentChange, users, loading, selectedUsers, onToggleUser, onToggleAll }) => {
  const allSelected = users.length > 0 && users.every(u => selectedUsers.has(u.id));
  return (
    <div className="space-y-3" data-testid="reset-user-selector">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5 min-w-[220px]">
          <Label className="text-sm font-medium">Department</Label>
          <Select value={department} onValueChange={onDepartmentChange}>
            <SelectTrigger data-testid="reset-department-select">
              <SelectValue placeholder="All departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="reset-department-option-all">All departments</SelectItem>
              {departments.map(d => (
                <SelectItem key={d} value={d} data-testid={`reset-department-option-${d}`}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground pb-2" data-testid="reset-selected-users-count">
          {selectedUsers.size} of {users.length} assigned user(s) selected
        </p>
      </div>

      <div className="rounded-md border">
        <label className="flex items-center gap-2 px-3 py-2 border-b bg-muted/40 text-sm font-medium cursor-pointer">
          <Checkbox checked={allSelected} onCheckedChange={onToggleAll} disabled={!users.length} data-testid="reset-select-all-users" />
          Select all
        </label>
        <ScrollArea className="h-[220px]">
          {loading ? (
            <div className="flex items-center justify-center h-[200px]">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-10" data-testid="reset-no-users">
              No users assigned to this course{department !== 'all' ? ' in this department' : ''}
            </p>
          ) : (
            users.map(u => (
              <label key={u.id} className="flex items-center gap-3 px-3 py-2 border-b last:border-b-0 text-sm cursor-pointer hover:bg-muted/30">
                <Checkbox checked={selectedUsers.has(u.id)} onCheckedChange={() => onToggleUser(u.id)} data-testid={`reset-user-checkbox-${u.id}`} />
                <div className="min-w-0">
                  <p className="font-medium truncate">{u.name || u.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email} · {u.department}</p>
                </div>
                <div className="ml-auto flex items-center gap-2 shrink-0">
                  {u.has_certificate && <Award className="h-4 w-4 text-amber-500" />}
                  <Badge variant="outline" className={STATUS_STYLES[u.status] || ''}>{u.status.replace('_', ' ')}</Badge>
                  <span className="w-12 text-right text-xs text-muted-foreground">{Math.round(u.progress_percent || 0)}%</span>
                </div>
              </label>
            ))
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

export default ResetUserSelector;
